import React, { useState } from 'react';
import { createPublication } from '../api/api'; // Fonction API pour envoyer les données
import Sidebar from './Sidebare';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Avatar,
  Card,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import './PublicationForm.css';

const CreatePost = () => {
  const [content, setContent] = useState('');
  const { user } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');


  // Utilisation de la fonction useTheme et useMediaQuery pour vérifier la taille de l'écran
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // Vérifie si l'écran est plus petit que 'sm'

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(files);

    // Aperçu des images
    const filePreviews = files.map((file) => URL.createObjectURL(file));
    setPreview(filePreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    setLoading(true);
  
    try {
      const formData = new FormData();
    
      formData.append('content', content);
      attachments.forEach((file) => formData.append('attachments', file));
    
      // Vérifie le contenu de FormData pour le debug
      console.log('Contenu du FormData :');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }
  
      // 🔑 Récupérer le token depuis le localStorage
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Utilisateur non authentifié.');
      }
  
      // 🚀 Envoi de la requête avec le token dans l'en-tête
      await createPublication(formData, token);
  
      setSuccessMessage('Publication créée avec succès!');
      setContent('');
      setAttachments([]);
      setPreview([]);
    } catch (error) {
      console.error('Erreur lors de la création de la publication:', error);
      setErrorMessage('Erreur lors de la création de la publication.');
    } finally {
      setLoading(false);
    }
  };
  

  // Fonction pour annuler la publication en cours
  const handleCancel = () => {
    setContent('');
    setAttachments([]);
    setPreview([]);
    setSuccessMessage('');
    setErrorMessage('');
  };

  return (
    <Box display="flex" height="100vh">
      {/* Sidebar masqué sur les petits écrans */}
      {!isSmallScreen && <Sidebar userProfile={user} />}

      {/* Zone principale */}
      <Box flex={1} p={2} display="flex" flexDirection="column" alignItems="center">
        {/* Zone de création de publication */}
        <Card sx={{ maxWidth: 600, width: '100%', boxShadow: 3, mb: 2, p: 2 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar src={user?.photo} alt={user?.name} sx={{ width: 40, height: 40, mr: 2 }} />
            <Typography variant="h6">Que partage {user?.name} aujourd'hui ?</Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              multiline
              rows={3}
              placeholder="Publiez un événement ou un projet académique..."
              fullWidth
              value={content}
              onChange={(e) => setContent(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
            />

            {preview.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {preview.map((src, index) => (
                  <img 
                    key={index}
                    src={src}
                    alt="Prévisualisation"
                    style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: 8 }}
                  />
                ))}
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <IconButton component="label" color="primary">
                <PhotoCamera />
                <input hidden type="file" multiple accept="image/*" onChange={handleFileChange} />
              </IconButton>
              <Button type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Publier'}
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleCancel}>
                Annuler
              </Button>
            </Box>
          </form>
        </Card>

        {successMessage && <Typography variant="body2" color="success.main">{successMessage}</Typography>}
        {errorMessage && <Typography variant="body2" color="error.main">{errorMessage}</Typography>}
      </Box>
    </Box>
  );
};

export default CreatePost;
